import React, { useState } from 'react';
import { IntelligenceShell, MonoNum } from '../components/intelligence/IntelligenceShell';
import { useIntelligenceReport, KpiCard, ChartCard, RangeControl, ExportMenu, ReportHeader, ReportSkeleton, downloadCsv, exportPdf, tooltipStyle, CARD } from '../components/intelligence/ReportKit';
import { AiSuggestion } from '../components/intelligence/AiSuggestion';
import { useOperatorLocale } from '../hooks/useOperatorLocale';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';

export default function CustomerInsightsReport() {
  const [months, setMonths] = useState(12);
  const { data, loading } = useIntelligenceReport('customer-insights', months);
  const { formatMoney } = useOperatorLocale();
  const s = data?.summary;
  const monthly = (data?.monthlyNewReturning || []).map((m) => ({ ...m, month: m.month.slice(5) + '/' + m.month.slice(2, 4) }));

  const exportCsv = () => {
    if (!data) return;
    downloadCsv([
      ['Customer', 'Email', 'Bookings', 'Revenue', 'Last Trip'],
      ...data.topCustomers.map((c) => [c.name, c.email, c.bookings, c.revenue, c.lastTrip || '']),
    ], `top-customers-${data.periodStart}-to-${data.periodEnd}.csv`);
  };

  const exportPdfDoc = () => {
    if (!data) return;
    exportPdf({
      filename: `customer-insights-${data.periodStart}-to-${data.periodEnd}.pdf`,
      title: 'Customer Insights',
      subtitle: `Your bookings only · ${data.periodStart} to ${data.periodEnd}`,
      kpis: [
        ['Unique customers', String(s.uniqueCustomers)],
        ['Repeat rate', `${s.repeatRate}% (${s.repeatCustomers} repeat)`],
        ['Avg spend / customer', formatMoney(s.avgSpendPerCustomer)],
        ['Avg booking value', formatMoney(s.avgBookingValue)],
        ['Avg group size', String(s.avgPartySize ?? '-')],
      ],
      tables: [
        { title: 'Group size distribution', head: ['Group size', 'Bookings'], rows: data.partySizes.map((p) => [p.bucket, p.bookings]) },
        { title: 'New vs returning customers', head: ['Month', 'New', 'Returning'], rows: data.monthlyNewReturning.map((m) => [m.month, m.newCustomers, m.returning]) },
        { title: 'Top customers by revenue', head: ['Customer', 'Email', 'Trips', 'Revenue', 'Last trip'], rows: data.topCustomers.map((c) => [c.name, c.email, c.bookings, formatMoney(c.revenue), c.lastTrip || '-']) },
      ],
    });
  };

  return (
    <IntelligenceShell>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10" data-testid="customer-insights-report">
        <ReportHeader
          title="Customer Insights"
          blurb={`Who books with you over the last ${months} months — group sizes, repeat behavior and your highest-value customers.`}
          right={<><AiSuggestion report="customer-insights" months={months} /><RangeControl months={months} setMonths={setMonths} prefix="customers" /><ExportMenu onCsv={exportCsv} onPdf={exportPdfDoc} testid="customers-export" /></>}
        />

        {loading ? <ReportSkeleton /> : !data ? null : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
              <KpiCard delay={60} testid="kpi-cust-unique" label="Unique customers" value={s.uniqueCustomers.toLocaleString()} sub={`last ${data.periodMonths} months`} />
              <KpiCard delay={120} testid="kpi-cust-repeat" label="Repeat rate" value={`${s.repeatRate}%`} sub={`${s.repeatCustomers} booked more than once`} />
              <KpiCard delay={180} testid="kpi-cust-spend" label="Avg spend / customer" value={formatMoney(s.avgSpendPerCustomer)} sub={`${formatMoney(s.avgBookingValue)} per booking`} />
              <KpiCard delay={240} testid="kpi-cust-party" label="Avg group size" value={s.avgPartySize ?? '—'} sub="guests per booking" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
              <ChartCard delay={300} title="Group size distribution" testid="cust-party-chart">
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.partySizes} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                      <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip formatter={(v) => [`${v} bookings`, 'Group size']} contentStyle={tooltipStyle} cursor={{ fill: 'rgba(37,99,235,0.04)' }} />
                      <Bar dataKey="bookings" fill="#2563EB" radius={[8, 8, 0, 0]} barSize={36} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>

              <ChartCard delay={360} title="New vs returning customers" testid="cust-newret-chart">
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthly} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(37,99,235,0.04)' }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                      <Bar dataKey="newCustomers" name="New" stackId="c" fill="#2563EB" radius={[0, 0, 0, 0]} barSize={18} />
                      <Bar dataKey="returning" name="Returning" stackId="c" fill="#93C5FD" radius={[6, 6, 0, 0]} barSize={18} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>
            </div>

            <div className={`${CARD} overflow-x-auto intel-rise`} style={{ animationDelay: '420ms' }} data-testid="cust-top-table">
              <div className="px-6 pt-5 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">Top customers by revenue</div>
              <table className="w-full text-sm mt-2">
                <thead>
                  <tr className="border-b border-slate-100 text-left">
                    {['Customer', 'Trips', 'Revenue', 'Last trip'].map((h) => (
                      <th key={h} className="px-6 py-3.5 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.topCustomers.map((c) => (
                    <tr key={c.email} className="border-b border-slate-50 last:border-0 hover:bg-[#F7F9FC] transition-colors duration-150">
                      <td className="px-6 py-4">
                        <div className="font-bold text-slate-900">{c.name}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{c.email}</div>
                      </td>
                      <td className="px-6 py-4"><MonoNum className="text-slate-600">{c.bookings}</MonoNum></td>
                      <td className="px-6 py-4"><MonoNum className="font-bold text-slate-900">{formatMoney(c.revenue)}</MonoNum></td>
                      <td className="px-6 py-4"><MonoNum className="text-slate-600">{c.lastTrip || '—'}</MonoNum></td>
                    </tr>
                  ))}
                  {data.topCustomers.length === 0 && (
                    <tr><td colSpan={4} className="px-6 py-8 text-center text-sm text-slate-400">No customer bookings in this period.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </IntelligenceShell>
  );
}
